/** This file contains functions that convert between in-memory query format and bookmark format */

import find from 'lodash/find';
import pick from 'lodash/pick';

import {SESSION_EVENTS} from 'common/report/constants';
import {serializePropFilter, SerializePropFilterContext} from 'common/report/util/arb';
import {ArrayType} from 'common/util/typescript';
import {renameEvent} from 'common/report/util/events';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {
  BookmarkFunnelStep,
  BookmarkOrStep,
  FunnelBookmark,
  isLegacyAggregateBy,
  isOrStep,
  LegacyBookmarkFunnelStep,
} from 'common/report/models/funnels/types';
import {
  convertPropertyFilterToSegfilter,
  convertSegfilterToPropertyFilter,
  SegFilterProps,
} from 'common/widgets/property-filter-menu/models/segfilter';
import {FunnelAttribution} from 'common/widgets/query-builder/types';
import {
  FiltersOperator,
  isGroupedEntry,
  QueryEventEntry,
  QueryGroupedEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {Segmentation} from 'common/widgets/property-filter-menu/models/segfilter';
import {PropertyType, ResourceType} from 'common/report/constants';

export function mapBookmarkToHoldPropConstant(aggregateBy: FunnelBookmark['aggregate_by']): Segmentation[] {
  return aggregateBy.map((item) => {
    if (isLegacyAggregateBy(item)) {
      return {
        propertyName: item.raw,
        resourceType: ResourceType.Event,
        propertyType: PropertyType.String,
        propertyDefaultType: PropertyType.String,
      };
    } else {
      return item;
    }
  });
}

export function mapBookmarkToAttribution(actionMode: string, actionStep: number): FunnelAttribution {
  if (actionMode === `step`) {
    return {action_mode: actionMode, action_step: actionStep};
  } else if (actionMode === `first_touch` || actionMode === `last_touch`) {
    return {action_mode: actionMode};
  } else {
    return {action_mode: `last_touch`};
  }
}

export function mapBookmarkExclusionStepToEventEntry(
  eventCount: number,
  customEvents: CustomEventDefinition[],
  step: FunnelBookmark['exclusions'][number],
): QueryEventEntry {
  const entry = mapBookmarkStepToEventEntry(customEvents, step);

  const {from = 0, to = 0} = step.steps;
  let excludeRange, excludeAll;

  if (from === 1 && to === eventCount) {
    excludeAll = true;
    excludeRange = {};
  } else {
    excludeAll = false;
    excludeRange = {from, to};
  }

  return {
    ...entry,
    excludeRange,
    excludeAll,
  };
}

/** Deserialize bookmark steps, taking grouped steps into account. */
export function mapBookmarkStepsToEntries(
  customEvents: CustomEventDefinition[],
  steps: FunnelBookmark['steps'],
): Array<QueryEventEntry | QueryGroupedEntry> {
  return steps.map((step) => {
    if (isOrStep(step)) {
      return mapBookmarkOrStepToGroupedEntry(customEvents, step);
    } else {
      return mapBookmarkStepToEventEntry(customEvents, step);
    }
  });
}

export function mapBookmarkOrStepToGroupedEntry(
  customEvents: CustomEventDefinition[],
  step: BookmarkOrStep,
): QueryGroupedEntry {
  return {
    type: SelectorTypeDefinitions.GroupedEvent,
    event: step.compare.map(mapBookmarkStepToEventEntry.bind(null, customEvents)),
    filters: [],
  };
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function mapBookmarkStepToEventEntry(
  customEvents: CustomEventDefinition[],
  step: LegacyBookmarkFunnelStep,
): QueryEventEntry {
  const additionalAttributes: Partial<QueryEventEntry> = {};
  const isCustomEvent = Object.prototype.hasOwnProperty.call(step, `custom_event`);

  let stepFilters = [];
  if (`property_filter_params_list` in step) {
    stepFilters = step.property_filter_params_list;
  } else if (step.filter) {
    // Older bookmarks don't have property filter params list,
    // instead they have just one filter at the top level.
    const stepFilter = pick(step, SegFilterProps);
    if (typeof stepFilter.property === `string`) {
      // Some older bookmarks have even less information that needs to be translated.
      stepFilter.property = {
        name: stepFilter.property,
        type: stepFilter.type,
        source: `properties`,
      };
    }
    stepFilters = [stepFilter];
  }

  const filters = stepFilters.map(convertSegfilterToPropertyFilter);
  const flowsDirections = pick(step, `forward`, `reverse`);

  // determine label of the event
  // older funnels bookmarks don't store all the necessary information in them. So we need to guess what the current
  // non renamed label would be, and we consider it renamed if the stored label doesn't match that.
  const eventLabel = step.step_label || ``;
  const sessionEventKey =
    step.session_event &&
    Object.keys(SESSION_EVENTS).find((session) => SESSION_EVENTS[session][`session_event`] === step.session_event);

  let eventName;
  let collectEverythingBlob = {};
  if (step.custom_event) {
    const maybeCustomEvent = find(customEvents, [`id`, step.custom_event]);
    if (maybeCustomEvent) {
      eventName = maybeCustomEvent.name;
      if (`is_collect_everything_event` in maybeCustomEvent || `isCollectEverything` in maybeCustomEvent) {
        collectEverythingBlob = {
          isCollectEverything: !!maybeCustomEvent.is_collect_everything_event || !!maybeCustomEvent.isCollectEverything,
        };
      }
    } else {
      eventName = `Deleted Custom Event`;
    }
  } else if (sessionEventKey) {
    eventName = renameEvent(sessionEventKey);
  } else {
    eventName = renameEvent(step.event);
  }

  let eventId = {};
  if (isCustomEvent) {
    eventId = {id: step.custom_event};
  } else if (`event_id` in step) {
    eventId = {id: step.event_id};
  }

  // also check against step.event for older bookmarks before we performed renaming for newer properties.
  const isRenamed = eventLabel !== step.event && eventLabel !== eventName;
  const filtersOperator = `bool_op` in step ? step.bool_op : FiltersOperator.Or;

  return {
    ...additionalAttributes,
    ...flowsDirections,
    filters,
    type: ResourceType.Event,
    filtersOperator,
    event: {
      ...eventId,
      ...collectEverythingBlob,
      custom: isCustomEvent,
      label: eventName,
      value: sessionEventKey || step.event || step.custom_event_name,
      ...(isRenamed ? {renamed: eventLabel} : {}),
    },
  };
}

export function mapHoldPropConstantToBookmark(holdingPropertyConstant: Segmentation[]) {
  return holdingPropertyConstant;
}

export function mapEventEntryToBookmarkExclusionStep(
  eventsLength: number,
  propertySerializationInfo: SerializePropFilterContext,
  step: QueryEventEntry,
): ArrayType<FunnelBookmark['exclusions']> {
  const entry = mapEventEntryToBookmarkStep(propertySerializationInfo, step);

  let steps;
  if (step.excludeRange && `from` in step.excludeRange) {
    steps = {...step.excludeRange};
  } else if (step.excludeAll) {
    steps = {from: 1, to: eventsLength};
  }

  return {
    ...entry,
    steps,
  };
}

export function mapEntriesToBookmarkSteps(
  propertySerializationInfo: SerializePropFilterContext,
  steps: Array<QueryEventEntry | QueryGroupedEntry>,
): FunnelBookmark['steps'] {
  return steps.map((step) => {
    if (isGroupedEntry(step)) {
      return mapGroupedEventToBookmarkStep(propertySerializationInfo, step);
    } else {
      return mapEventEntryToBookmarkStep(propertySerializationInfo, step);
    }
  });
}

export function mapGroupedEventToBookmarkStep(
  propertySerializationInfo: SerializePropFilterContext,
  step: QueryGroupedEntry,
): BookmarkOrStep {
  return {
    compare: step.event.map(mapEventEntryToBookmarkStep.bind(null, propertySerializationInfo)),
  };
}

export function mapEventEntryToBookmarkStep(
  propertySerializationInfo: SerializePropFilterContext,
  step: QueryEventEntry,
): BookmarkFunnelStep {
  const {event, filters, filtersOperator} = step;
  const segFilters = filters.map((filter) => convertPropertyFilterToSegfilter(filter));
  const selectors = segFilters.map((filter) => serializePropFilter(filter, propertySerializationInfo));
  const serialized = selectors.join(` ${filtersOperator} `);
  let eventProperties: Partial<FunnelBookmark['steps'][number]> = {
    step_label: event.renamed || event.label,
    ...pick(step, `forward`, `reverse`),
  };

  // generate event properties
  if (event.custom) {
    eventProperties = {
      ...eventProperties,
      custom_event: event.id,
      custom_event_name: event.value,
    };
  } else if (SESSION_EVENTS[event.value]) {
    eventProperties = {
      ...SESSION_EVENTS[event.value],
      ...eventProperties,
    };
  } else {
    eventProperties = {
      ...eventProperties,
      event: event.value,
      ...(event.id ? {event_id: event.id} : {}),
    };
  }

  return {
    ...eventProperties,
    ...(selectors.length ? {serialized} : {}),
    bool_op: filtersOperator,
    property_filter_params_list: segFilters,
  };
}
